import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { ApiError } from '@/api/client'
import { formatDate, formatMoney } from '@/components/common'
import { AvailabilityBoard, today } from './AvailabilityBoard'
import { customerServiceName, customerServiceNote, serviceArt } from './serviceArt'
import {
  portalApi,
  type PortalEstimate,
  type PortalPackage,
  type PortalPlan,
  type PortalQuotation,
  type PortalService,
} from './portalApi'

const STEPS = ['What kind of event', 'Date and place', 'Package and menu', 'What it comes to']
const LAST = STEPS.length - 1
const OCCASIONS = ['Wedding', 'Reception', 'Engagement', 'Birthday', 'Corporate', 'Other']

/**
 * A customer putting an event together and asking for it in writing.
 *
 * <p>Three steps, in the order the decisions are actually made: which day and
 * which sitting, then what to serve, then what it comes to. The price is worked
 * out by the server on every change - the same code that prices a quotation -
 * so the figure shown here is not the browser's arithmetic and cannot drift
 * from what the office sees.</p>
 *
 * <p>What is sent is a request, not an agreement. It reaches the office as a
 * real enquiry and a real quotation at list rates, for someone to look at.</p>
 */
export function PlanEventDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean
  onClose: () => void
  onDone: () => void
}) {
  const [step, setStep] = useState(0)
  const [service, setService] = useState<PortalService | null>(null)
  const [address, setAddress] = useState('')
  const [eventType, setEventType] = useState('Wedding')
  const [date, setDate] = useState(today())
  const [guests, setGuests] = useState('200')
  const [hallId, setHallId] = useState<string | null>(null)
  const [hallName, setHallName] = useState<string | null>(null)
  const [slotId, setSlotId] = useState<string | null>(null)
  const [slotName, setSlotName] = useState<string | null>(null)
  const [packageVersionId, setPackageVersionId] = useState<string | null>(null)
  const [chosen, setChosen] = useState<Record<string, string[]>>({})
  const [notes, setNotes] = useState('')
  const [estimate, setEstimate] = useState<PortalEstimate | null>(null)
  const [pricing, setPricing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState<PortalQuotation | null>(null)

  const services = useQuery({
    queryKey: ['portal', 'services'],
    queryFn: () => portalApi.get<PortalService[]>('/services'),
    enabled: open,
  })

  // Only the packages sold for the chosen line of business. Offering a banquet
  // package to somebody ordering a takeaway is offering something we will not
  // then supply.
  const packages = useQuery({
    queryKey: ['portal', 'packages', service?.code],
    queryFn: () => portalApi.get<PortalPackage[]>(`/packages?serviceType=${service?.code}`),
    enabled: open && Boolean(service),
  })

  const selected = useMemo(
    () => (packages.data ?? []).find((offer) => offer.packageVersionId === packageVersionId) ?? null,
    [packages.data, packageVersionId],
  )

  const plan = useMemo<PortalPlan | null>(() => {
    if (!packageVersionId || !date || !service) return null
    return {
      serviceType: service.code,
      eventType,
      eventDate: date,
      guestCount: Number(guests) || 0,
      hallId: hallId ?? undefined,
      hallSlotId: slotId ?? undefined,
      deliveryAddress: address || undefined,
      packageId: selected?.packageId,
      packageVersionId,
      menuSelections: Object.entries(chosen).flatMap(([packageItemId, ids]) =>
        ids.map((menuItemId) => ({ menuItemId, packageItemId })),
      ),
      notes: notes || undefined,
    }
  }, [service, eventType, date, guests, hallId, slotId, address, selected, packageVersionId,
      chosen, notes])

  // Priced by the server on every change, so what is shown is what the office
  // would quote rather than a number the browser worked out for itself.
  useEffect(() => {
    if (!open || step !== LAST || !plan) return
    let stale = false
    setPricing(true)
    portalApi
      .post<PortalEstimate>('/quotations/estimate', plan)
      .then((result) => {
        if (!stale) setEstimate(result)
      })
      .catch((problem: unknown) => {
        if (!stale) {
          setEstimate(null)
          setError(problem instanceof ApiError ? problem.message : 'Could not price that.')
        }
      })
      .finally(() => {
        if (!stale) setPricing(false)
      })
    return () => {
      stale = true
    }
  }, [open, step, plan])

  const reset = () => {
    setStep(0)
    setSent(null)
    setEstimate(null)
    setError(null)
    setChosen({})
    setPackageVersionId(null)
    setHallId(null)
    setSlotId(null)
    setService(null)
    setAddress('')
  }

  /** Choosing a different service invalidates everything chosen under the old one. */
  const chooseService = (chosenService: PortalService) => {
    setService(chosenService)
    setPackageVersionId(null)
    setChosen({})
    setHallId(null)
    setSlotId(null)
    setSlotName(null)
    setHallName(null)
    setAddress('')
    setStep(1)
  }

  const close = () => {
    onClose()
    reset()
  }

  const toggle = (packageItemId: string, menuItemId: string, limit: number) => {
    setChosen((current) => {
      const picked = current[packageItemId] ?? []
      if (picked.includes(menuItemId)) {
        return { ...current, [packageItemId]: picked.filter((id) => id !== menuItemId) }
      }
      // Beyond the limit the oldest choice makes way, which is friendlier than
      // refusing the click and saying nothing.
      const next = limit > 0 && picked.length >= limit ? picked.slice(1) : picked
      return { ...current, [packageItemId]: [...next, menuItemId] }
    })
  }

  const send = async () => {
    if (!plan) return
    setSending(true)
    setError(null)
    try {
      const quotation = await portalApi.post<PortalQuotation>('/quotations', plan)
      setSent(quotation)
      onDone()
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'Could not send that just now.')
    } finally {
      setSending(false)
    }
  }

  const booksASpace = Boolean(service?.spaceType)
  const canLeaveDate =
    Boolean(date) &&
    Number(guests) > 0 &&
    (!service?.needsDeliveryAddress || address.trim().length > 0)
  const canLeavePackage = Boolean(packageVersionId)
  const canGoOn = step === 0 ? Boolean(service) : step === 1 ? canLeaveDate : canLeavePackage

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle>Plan your event</DialogTitle>
      <DialogContent>
        {sent ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Sent to our office as {sent.quotationNumber}.
            </Typography>
            <Typography variant="body2">
              It is in your Quotations tab at {formatMoney(sent.totalAmount)}. Someone will confirm
              it and send you the priced copy to accept.
            </Typography>
          </Alert>
        ) : (
          <>
            <Stepper activeStep={step} sx={{ my: 2 }}>
              {STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : null}

            {step === 0 ? (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  What kind of event is it? This settles where it is held and what we
                  can offer.
                </Typography>
                {services.data?.length === 0 ? (
                  <Alert severity="info">
                    Nothing can be booked online yet. Please call the office.
                  </Alert>
                ) : null}
                <Grid container spacing={2} sx={{ mt: 0 }}>
                  {(services.data ?? []).map((offer) => (
                    <Grid item xs={12} sm={6} key={offer.code}>
                      <Card
                        variant="outlined"
                        onClick={() => chooseService(offer)}
                        sx={{
                          cursor: 'pointer',
                          borderColor: service?.code === offer.code ? 'primary.main' : undefined,
                          borderWidth: service?.code === offer.code ? 2 : 1,
                        }}
                      >
                        {serviceArt(offer.code) ? (
                          <Box
                            component="img"
                            src={serviceArt(offer.code)}
                            alt=""
                            sx={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
                          />
                        ) : null}
                        <CardContent>
                          <Typography variant="subtitle1">
                            {customerServiceName(offer.code, offer.name)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {offer.code === 'COUNTER_SALE'
                              ? customerServiceNote(offer.code, '')
                              : offer.spaceType === 'HALL'
                              ? 'In one of our banquet halls'
                              : offer.spaceType === 'DINING'
                                ? 'In our restaurant'
                                : offer.needsDeliveryAddress
                                  ? 'We come to you'
                                  : 'Ask us and we will arrange it'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            ) : null}

            {step === 1 ? (
              <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Occasion"
                      value={eventType}
                      onChange={(event) => setEventType(event.target.value)}
                    >
                      {OCCASIONS.map((occasion) => (
                        <MenuItem key={occasion} value={occasion}>
                          {occasion}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Date"
                      value={date}
                      onChange={(event) => {
                        setDate(event.target.value)
                        setSlotId(null)
                        setHallId(null)
                      }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: today() }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="How many guests"
                      value={guests}
                      onChange={(event) => setGuests(event.target.value)}
                    />
                  </Grid>
                </Grid>

                {booksASpace ? (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      Sessions free on this date — pick one, or carry on without
                    </Typography>
                    <AvailabilityBoard
                      date={date}
                      serviceType={service?.code ?? ''}
                      selectedSlotId={slotId}
                      onPick={(hall, slot) => {
                        setHallId(hall.hallId)
                        setHallName(hall.hallName)
                        setSlotId(slot.slotId)
                        setSlotName(slot.name)
                      }}
                    />
                  </>
                ) : (
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    label="Where should we serve?"
                    placeholder="House name or number, street, area, town"
                    value={address}
                    onChange={(event) => setAddress(event.target.value)}
                    required={service?.needsDeliveryAddress}
                    helperText={
                      service?.needsDeliveryAddress
                        ? 'We come to you for this, so we need the address.'
                        : 'Optional.'
                    }
                  />
                )}
              </>
            ) : null}

            {step === 2 ? (
              <>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Package"
                  value={packageVersionId ?? ''}
                  onChange={(event) => {
                    setPackageVersionId(event.target.value)
                    setChosen({})
                  }}
                  sx={{ mb: 2 }}
                  helperText={
                    packages.data?.length === 0
                      ? 'No packages are on sale online. Please call the office.'
                      : 'Prices are per guest, at list rates.'
                  }
                >
                  {(packages.data ?? []).map((offer) => (
                    <MenuItem key={offer.packageVersionId} value={offer.packageVersionId}>
                      {offer.name} — {formatMoney(offer.pricePerPerson)} a head
                    </MenuItem>
                  ))}
                </TextField>

                {selected?.choices.map((choice) => {
                  const picked = chosen[choice.packageItemId] ?? []
                  const short = picked.length < choice.minSelections
                  return (
                    <Box key={choice.packageItemId} sx={{ mb: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2">
                          {choice.itemNumber}. {choice.label}
                        </Typography>
                        <Chip
                          size="small"
                          color={short ? 'warning' : 'default'}
                          label={
                            choice.minSelections === choice.maxSelections
                              ? `pick ${choice.maxSelections}`
                              : `pick ${choice.minSelections}–${choice.maxSelections}`
                          }
                        />
                      </Stack>
                      <Stack direction="row" flexWrap="wrap" useFlexGap>
                        {choice.items.map((item) => (
                          <FormControlLabel
                            key={`${item.menuItemId}-${item.variantId ?? ''}`}
                            sx={{ width: { xs: '100%', sm: '48%' } }}
                            control={
                              <Checkbox
                                size="small"
                                checked={picked.includes(item.menuItemId)}
                                onChange={() =>
                                  toggle(
                                    choice.packageItemId,
                                    item.menuItemId,
                                    choice.maxSelections,
                                  )
                                }
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {item.name}
                                {item.mandatory ? ' (always served)' : ''}
                              </Typography>
                            }
                          />
                        ))}
                      </Stack>
                    </Box>
                  )
                })}
              </>
            ) : null}

            {step === 3 ? (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {service?.name}: {eventType} on {formatDate(date)} for {guests} guests
                  {slotName ? ` — ${slotName} at ${hallName}` : ''}
                  {address ? ` — ${address}` : ''}
                </Typography>

                {pricing ? <Typography variant="body2">Working it out…</Typography> : null}

                {estimate ? (
                  <>
                    {estimate.issues.length > 0 ? (
                      <Alert severity={estimate.valid ? 'info' : 'warning'} sx={{ my: 2 }}>
                        {estimate.issues.map((issue) => (
                          <div key={issue}>{issue}</div>
                        ))}
                      </Alert>
                    ) : null}

                    <Stack spacing={0.5} sx={{ my: 2 }}>
                      {estimate.lines.map((line, index) => (
                        <Row key={index} label={line.description} value={formatMoney(line.amount)} />
                      ))}
                      <Divider sx={{ my: 1 }} />
                      <Row label="Subtotal" value={formatMoney(estimate.subtotal)} />
                      <Row
                        label={`Tax at ${estimate.taxRatePercent}%`}
                        value={formatMoney(estimate.taxAmount)}
                      />
                      <Divider sx={{ my: 1 }} />
                      <Row label="Total" value={formatMoney(estimate.total)} strong />
                    </Stack>

                    <Alert severity="info">
                      This is what your choices come to at list prices. It is not a confirmed
                      offer until our office has checked the date and sent it to you.
                    </Alert>
                  </>
                ) : null}

                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  label="Anything we should know"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  sx={{ mt: 2 }}
                />
              </>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {sent ? (
          <Button variant="contained" onClick={close}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={close}>Cancel</Button>
            {step > 0 ? <Button onClick={() => setStep(step - 1)}>Back</Button> : null}
            {step < LAST ? (
              <Button variant="contained" disabled={!canGoOn} onClick={() => setStep(step + 1)}>
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                disabled={sending || pricing || !estimate?.valid}
                onClick={() => void send()}
              >
                Send this to the office
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" sx={{ fontWeight: strong ? 700 : 400 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: strong ? 700 : 400 }}>
        {value}
      </Typography>
    </Stack>
  )
}
